import { recordAttributionEventToCloud } from './cloudService';

const VISITOR_ID_KEY = 'clero_visitor_id';
const VISITOR_TOKEN_KEY = 'clero_visitor_token';
const VISITOR_CREATED_KEY = 'clero_visitor_created';
const LAST_ATTRIBUTION_KEY = 'clero_last_attribution';
const COLLECTION_EVENTS = 'webAttributionEvents';
const COLLECTION_VISITORS = 'webAttributionVisitors';

export type AttributionDimension = 'source' | 'campaign' | 'medium' | 'param';

export type AttributionNormalized = {
  source: string;
  campaign: string;
  medium: string;
  term: string;
  content: string;
};

export type AttributionSnapshot = {
  visitorId: string;
  visitorToken?: string;
  path: string;
  search: string;
  queryParams: Record<string, string>;
  normalized: AttributionNormalized;
  referrer: string;
  hostname: string;
  userAgent: string;
  eventAt: string;
};

const safeLower = (value: unknown): string => String(value || '').trim().toLowerCase();

const normalizeField = (params: Record<string, string>, keys: string[]): string => {
  for (const key of keys) {
    const value = String(params[key] || '').trim();
    if (value) return value;
  }
  return '';
};

const ALLOWED_ATTRIBUTION_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'ref',
  'source',
  'src',
  'channel',
  'cmp',
  'medium',
  'campaign',
  'term',
  'content',
]);

const parseSafeQueryParams = (search: string): { queryParams: Record<string, string>; safeSearch: string } => {
  const parsed = new URLSearchParams(search || '');
  const map: Record<string, string> = {};
  const safeParams = new URLSearchParams();

  parsed.forEach((value, key) => {
    const cleanKey = String(key || '').trim().toLowerCase();
    const cleanValue = String(value || '').trim();
    if (ALLOWED_ATTRIBUTION_KEYS.has(cleanKey) && cleanValue) {
      map[cleanKey] = cleanValue;
      safeParams.append(cleanKey, cleanValue);
    }
  });

  const safeSearchString = safeParams.toString();
  return {
    queryParams: map,
    safeSearch: safeSearchString ? `?${safeSearchString}` : '',
  };
};

const getVisitorCredentials = (): { visitorId: string; visitorToken: string } => {
  let visitorId = String(window.localStorage.getItem(VISITOR_ID_KEY) || '').trim();
  let visitorToken = String(window.localStorage.getItem(VISITOR_TOKEN_KEY) || '').trim();

  // If either visitorId or visitorToken is missing, generate a fresh pair.
  // This guarantees returning visitors with legacy tokenless IDs are cleanly migrated
  // to a new paired identity rather than sending unverified events that fail tokenHash checks.
  if (!visitorId || !visitorToken) {
    visitorId = `wv_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
    visitorToken = `vt_${Date.now()}_${Math.random().toString(36).slice(2, 16)}_${Math.random().toString(36).slice(2, 16)}`;
    window.localStorage.setItem(VISITOR_ID_KEY, visitorId);
    window.localStorage.setItem(VISITOR_TOKEN_KEY, visitorToken);
  }
  return { visitorId, visitorToken };
};

const getVisitorId = (): string => {
  return getVisitorCredentials().visitorId;
};

const buildSnapshot = (credentials: { visitorId: string; visitorToken: string } = getVisitorCredentials()): AttributionSnapshot => {
  const path = String(window.location.pathname || '/').trim() || '/';
  const { queryParams, safeSearch } = parseSafeQueryParams(window.location.search);
  const normalized: AttributionNormalized = {
    source: normalizeField(queryParams, ['utm_source', 'source', 'src', 'ref', 'channel']),
    campaign: normalizeField(queryParams, ['utm_campaign', 'campaign', 'cmp']),
    medium: normalizeField(queryParams, ['utm_medium', 'medium']),
    term: normalizeField(queryParams, ['utm_term', 'term']),
    content: normalizeField(queryParams, ['utm_content', 'content']),
  };

  return {
    visitorId: credentials.visitorId,
    visitorToken: credentials.visitorToken,
    path,
    search: safeSearch,
    queryParams,
    normalized,
    referrer: String(document.referrer || '').trim(),
    hostname: String(window.location.hostname || '').trim(),
    userAgent: String(window.navigator.userAgent || '').trim(),
    eventAt: new Date().toISOString(),
  };
};

const persistLastSnapshot = (snapshot: AttributionSnapshot) => {
  try {
    window.localStorage.setItem(LAST_ATTRIBUTION_KEY, JSON.stringify(snapshot));
  } catch (_error) {
    // No-op: attribution should never block app flows.
  }
};

export const getLatestAttributionSnapshot = (): AttributionSnapshot | null => {
  try {
    const raw = String(window.localStorage.getItem(LAST_ATTRIBUTION_KEY) || '').trim();
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AttributionSnapshot;
    if (!parsed || !parsed.visitorId) return null;
    return parsed;
  } catch (_error) {
    return null;
  }
};

export const trackWebAttributionEvent = async (): Promise<void> => {
  if (typeof window === 'undefined') return;

  const path = safeLower(window.location.pathname);
  if (path === '/admin') return;

  const credentials = getVisitorCredentials();
  const snapshot = buildSnapshot(credentials);
  persistLastSnapshot(snapshot);

  try {
    await recordAttributionEventToCloud({
      visitorId: snapshot.visitorId,
      visitorToken: snapshot.visitorToken || '',
      path: snapshot.path,
      search: snapshot.search,
      queryParams: snapshot.queryParams,
      normalized: snapshot.normalized,
      referrer: snapshot.referrer,
      hostname: snapshot.hostname,
      userAgent: snapshot.userAgent,
      eventAt: snapshot.eventAt,
    });
  } catch (_error) {
    // No-op: attribution should never block app flows.
  }
};
